import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getLiveStatus, animationFileUrl, sendDynamicText, triggerManualAnimation, ManualAnimation, getStaticElements, ttsElevenLabs } from './api';
import QuestionQueuePanel, { gLabelStyle } from './QuestionQueuePanel';

type CameraFacing = 'user' | 'environment';

interface MobileCommentItem {
  username?: string;
  nickname?: string;
  avatar_url?: string;
  text?: string;
}

interface AnimEvent {
  file?: string | null;
  animation_file?: string | null;
  animation_type?: 'video' | 'audio' | 'text' | 'text_dynamic' | 'mobile_comments';
  text?: string | null;
  text_color?: string;
  text_size?: number;
  text_effect?: string;
  text_duration?: number;
  text_font?: string;
  text_loops?: number;
  text_bg_color?: string;
  text_bg_opacity?: number;
  text_bg_anim?: string;
  pos_x: number; pos_y: number;
  pos_w: number; pos_h: number;
  gift_name?: string | null;
  show_gift_image?: boolean | number;
  gift_image_url?: string | null;
  gift_img_x?: number;
  gift_img_y?: number;
  gift_img_w?: number;
  gift_img_h?: number;
}

interface StaticElementDisplay {
  id: number; image_file: string;
  pos_x: number; pos_y: number; pos_w: number; pos_h: number;
  layer: 'above' | 'below'; sort_order: number;
}

interface DynamicTextConfig {
  id: number;
  pos_x: number; pos_y: number; pos_w: number; pos_h: number;
  text_color: string;
  text_size: number;
  text_effect: string;
  text_duration: number;
  text_font?: string;
  text_loops?: number;
  text_bg_color?: string;
  text_bg_opacity?: number;
  text_bg_anim?: string;
}

interface BannerOverlayItem {
  theme_animation_id: number;
  name?: string;
  pos_x: number; pos_y: number; pos_w: number; pos_h: number;
  sort_order?: number;
  config?: Record<string, any>;
}

const TEXT_ANIMATION_CSS = `
@keyframes mo-fade{from{opacity:0}50%{opacity:1}to{opacity:0}}
@keyframes mo-scroll-left{from{transform:translateX(110%)}to{transform:translateX(-110%)}}
@keyframes mo-scroll-right{from{transform:translateX(-110%)}to{transform:translateX(110%)}}
@keyframes mo-scroll-up{from{transform:translateY(110%)}to{transform:translateY(-110%)}}
@keyframes mo-scroll-down{from{transform:translateY(-110%)}to{transform:translateY(110%)}}
@keyframes mo-bounce{0%,100%{transform:translateY(0)}25%{transform:translateY(-14px)}75%{transform:translateY(-7px)}}
@keyframes mo-zoom{0%{opacity:0;transform:scale(0.2)}20%{opacity:1;transform:scale(1)}80%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(0.2)}}
@keyframes mo-typewriter{from{clip-path:inset(0 100% 0 0)}to{clip-path:inset(0 0% 0 0)}}
@keyframes mo-bg-pivot-h{from{transform-origin:left center;transform:scaleX(0)}to{transform-origin:left center;transform:scaleX(1)}}
@keyframes mo-bg-pivot-h-r{from{transform-origin:right center;transform:scaleX(0)}to{transform-origin:right center;transform:scaleX(1)}}
@keyframes mo-bg-pivot-v{from{transform-origin:top center;transform:scaleY(0)}to{transform-origin:top center;transform:scaleY(1)}}
@keyframes mo-bg-pivot-v-b{from{transform-origin:bottom center;transform:scaleY(0)}to{transform-origin:bottom center;transform:scaleY(1)}}
@keyframes mo-bg-expand{from{transform:scaleX(0)}to{transform:scaleX(1)}}
@keyframes mo-bg-expand-v{from{transform:scaleY(0)}to{transform:scaleY(1)}}
@keyframes mo-bg-fade{from{opacity:0}to{opacity:1}}
@keyframes mo-bg-slide-l{from{transform:translateX(-100%)}to{transform:translateX(0)}}
@keyframes mo-bg-slide-r{from{transform:translateX(100%)}to{transform:translateX(0)}}
@keyframes mo-bg-slide-t{from{transform:translateY(-100%)}to{transform:translateY(0)}}
@keyframes mo-bg-slide-b{from{transform:translateY(100%)}to{transform:translateY(0)}}
@keyframes qmo-fade{from{opacity:0}to{opacity:1}}
@keyframes qmo-zoom{from{opacity:0;transform:scale(.5)}to{opacity:1;transform:scale(1)}}
@keyframes qmo-slide{from{opacity:0;transform:translateY(40px)}to{opacity:1;transform:translateY(0)}}
@keyframes qmo-flip{from{opacity:0;transform:perspective(600px) rotateX(90deg)}to{opacity:1;transform:perspective(600px) rotateX(0)}}
@keyframes bn-left{from{transform:translateX(115%)}to{transform:translateX(-115%)}}
@keyframes bn-right{from{transform:translateX(-115%)}to{transform:translateX(115%)}}
@keyframes bn-up{from{transform:translateY(115%)}to{transform:translateY(-115%)}}
@keyframes bn-down{from{transform:translateY(-115%)}to{transform:translateY(115%)}}
@keyframes bn-type{from{clip-path:inset(0 100% 0 0)}to{clip-path:inset(0 0 0 0)}}
@keyframes bn-fade{0%{opacity:0}15%,85%{opacity:1}100%{opacity:0}}
.mo-text-anim-wrap{position:absolute;overflow:hidden;pointer-events:none}
.mo-text-anim-bg{position:absolute;inset:0}
.mo-text-anim{position:absolute;inset:0;font-weight:700;text-shadow:2px 2px 6px rgba(0,0,0,0.8);word-break:break-word;display:flex;align-items:center;justify-content:center;text-align:center;overflow:hidden}
`;

interface Props { streamerId: number; }

function bannerRgba(color: string, opacity: number) {
  const op = Math.max(0, Math.min(100, Number(opacity || 0))) / 100;
  if (op <= 0) return 'transparent';
  const c = color || '#000000';
  const r = parseInt(c.slice(1, 3), 16) || 0;
  const g = parseInt(c.slice(3, 5), 16) || 0;
  const b = parseInt(c.slice(5, 7), 16) || 0;
  return `rgba(${r},${g},${b},${op})`;
}

function MobileBanner({ banner, spx }: { banner: BannerOverlayItem; spx: (v: number, m?: number, min?: number) => number }) {
  const cfg = banner.config || {};
  const patterns = Array.isArray(cfg.patterns) && cfg.patterns.length ? cfg.patterns : [{ text: cfg.text || banner.name || '' }];
  const [idx, setIdx] = useState(0);
  const pattern = patterns[idx % patterns.length] || {};
  const dur = Math.max(1200, Number(pattern.duration || cfg.duration || 9000));

  useEffect(() => {
    const t = setTimeout(() => setIdx(i => i + 1), dur + 120);
    return () => clearTimeout(t);
  }, [idx, dur, patterns.length]);

  const effect = String(pattern.effect || cfg.effect || 'scroll-left');
  const keyframes: Record<string, string> = {
    'scroll-left': 'bn-left',
    'scroll-right': 'bn-right',
    'scroll-up': 'bn-up',
    'scroll-down': 'bn-down',
    typewriter: 'bn-type',
    fade: 'bn-fade',
  };
  return (
    <div style={{
      position: 'absolute',
      left: `${banner.pos_x ?? 0}%`,
      top: `${banner.pos_y ?? 82}%`,
      width: `${banner.pos_w ?? 100}%`,
      height: `${banner.pos_h ?? 10}%`,
      zIndex: cfg.layer === 'above_queue' ? 8 : 5,
      overflow: 'hidden',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: bannerRgba(String(cfg.bg_color || '#000000'), Number(cfg.bg_opacity ?? 0)) }} />
      <div
        key={`${banner.theme_animation_id}-${idx}`}
        style={{
          position: 'relative',
          color: String(pattern.color || cfg.color || '#ffffff'),
          fontFamily: String(pattern.font || cfg.font || 'sans-serif'),
          fontSize: spx(Number(pattern.size || cfg.size || 42), 0.36, 10),
          fontWeight: 900,
          lineHeight: 1.15,
          whiteSpace: pattern.multiline || cfg.multiline ? 'pre-wrap' : 'pre',
          textShadow: '0 2px 8px rgba(0,0,0,.85)',
          animation: `${keyframes[effect] || 'bn-left'} ${dur / 1000}s linear 1 forwards`,
        }}
      >
        {String(pattern.text || cfg.text || banner.name || '')}
      </div>
    </div>
  );
}

export default function MobileOverlay({ streamerId }: Props) {
  const [liveStatus, setLiveStatus] = useState<'idle' | 'connecting' | 'connected' | 'error' | 'disconnected'>('idle');
  const [liveRunning, setLiveRunning] = useState(false);
  const [liveMsg, setLiveMsg] = useState('');
  const [overlayUrl, setOverlayUrl] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [currentAnim, setCurrentAnim] = useState<AnimEvent | null>(null);
  const [lastGift, setLastGift] = useState<string | null>(null);
  const [stageScale, setStageScale] = useState(() => {
    if (typeof window === 'undefined') return 1;
    return Math.min(window.innerWidth / 200, window.innerHeight / 356);
  });

  const [cameraOn, setCameraOn] = useState(false);
  const [cameraFacing, setCameraFacing] = useState<CameraFacing>('user');
  const [cameraError, setCameraError] = useState('');

  // Animations manuelles (table de mixage)
  const [manualAnims, setManualAnims] = useState<ManualAnimation[]>([]);
  const [triggeringId, setTriggeringId] = useState<number | null>(null);
  const [activePadId, setActivePadId] = useState<number | null>(null);
  const [selectedDynAnim, setSelectedDynAnim] = useState<ManualAnimation | null>(null);
  const [showMixer, setShowMixer] = useState(false);

  // Animations texte dynamiques
  const [dynamicTextConfigs, setDynamicTextConfigs] = useState<DynamicTextConfig[]>([]);
  const [showTextPanel, setShowTextPanel] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [textSending, setTextSending] = useState(false);
  // Parametres texte dynamique (modifiables en live depuis le panel)
  const [dynTextSize, setDynTextSize] = useState(48);
  const [dynTextColor, setDynTextColor] = useState('#ffffff');
  const [dynTextEffect, setDynTextEffect] = useState('fade');
  const [dynTextDuration, setDynTextDuration] = useState(5);
  // Animations texte actives (affichage dans l overlay)
  const [textAnims, setTextAnims] = useState<Array<{id: number; anim: AnimEvent}>>([]);
  const textAnimIdRef = useRef(0);
  // Éléments statiques du thème actif
  const [mobileCommentsEnabled, setMobileCommentsEnabled] = useState(false);
  const [mobileCommentsTts, setMobileCommentsTts] = useState(false);
  const [mobileCommentsCfg, setMobileCommentsCfg] = useState<Record<string, any> | null>(null);
  const [mobileComments, setMobileComments] = useState<MobileCommentItem[]>([]);
  const [qData, setQData] = useState<{ enabled: boolean; paused: boolean; config: Record<string, any>; items: any[] } | null>(null);
  const [qActive, setQActive] = useState<any | null>(null);
  const [qTick, setQTick] = useState(0);
  const qHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [staticElems, setStaticElems] = useState<StaticElementDisplay[]>([]);
  const [banners, setBanners] = useState<BannerOverlayItem[]>([]);
  // Images cadeaux permanentes (indicateurs viewer)
  const [permanentGifts, setPermanentGifts] = useState<Array<{gift_image_url: string; gift_img_x: number; gift_img_y: number; gift_img_w: number; gift_img_h: number; gift_name: string|null}>>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraRef = useRef<HTMLVideoElement>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const queueRef = useRef<AnimEvent[]>([]);
  const playingRef = useRef(false);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spx = useCallback((v: number, m = 1, min = 1) => Math.max(min, Math.round(v * m * stageScale)), [stageScale]);

  useEffect(() => {
    const updateScale = () => setStageScale(Math.min(window.innerWidth / 200, window.innerHeight / 356));
    updateScale();
    window.addEventListener('resize', updateScale);
    window.visualViewport?.addEventListener('resize', updateScale);
    window.addEventListener('orientationchange', updateScale);
    return () => {
      window.removeEventListener('resize', updateScale);
      window.visualViewport?.removeEventListener('resize', updateScale);
      window.removeEventListener('orientationchange', updateScale);
    };
  }, []);

  const normalizeAnimEvent = (data: any): AnimEvent => ({
    ...data,
    animation_file: data.animation_file ?? data.file ?? null,
    animation_type: data.animation_type ?? 'video',
    pos_x: Number(data.pos_x ?? 0),
    pos_y: Number(data.pos_y ?? 0),
    pos_w: Number(data.pos_w ?? 50),
    pos_h: Number(data.pos_h ?? 30),
  });

  // Load initial status
  const loadStatus = useCallback(async () => {
    try {
      const r = await getLiveStatus(streamerId);
      const s = r.data.status;
      const normalized = (!r.data.running && (s === 'error' || s === 'disconnected')) ? 'idle' : s;
      setLiveStatus(normalized);
      setLiveRunning(r.data.running);
      if (r.data.overlay_url) setOverlayUrl(r.data.overlay_url);
      if (r.data.display_name) setDisplayName(r.data.display_name);
      if (r.data.dynamic_text_configs) {
        setDynamicTextConfigs(r.data.dynamic_text_configs);
        if (r.data.dynamic_text_configs.length > 0) {
          const cfg = r.data.dynamic_text_configs[0];
          setDynTextSize(cfg.text_size);
          setDynTextColor(cfg.text_color);
          setDynTextEffect(cfg.text_effect);
          setDynTextDuration(Math.round(cfg.text_duration / 1000));
        }
      }
      if (r.data.manual_animations) setManualAnims(r.data.manual_animations);
      if (Array.isArray(r.data.banners)) setBanners(r.data.banners);
      // Charger les éléments statiques du thème actif
      if (r.data.active_theme_id) {
        try {
          const se = await getStaticElements(r.data.active_theme_id);
          setStaticElems(se.data as StaticElementDisplay[]);
        } catch {}
        // Chargement des images cadeaux permanentes (endpoint public)
        try {
          const gr = await fetch(`/nsstream/${streamerId}/gift-display`);
          if (gr.ok) setPermanentGifts(await gr.json());
        } catch {}
      } else {
        setStaticElems([]);
        setPermanentGifts([]);
      }
    } catch {}
  }, [streamerId]);

  // Camera
  const startCamera = useCallback(async (facing: CameraFacing) => {
    setCameraError('');
    try {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(t => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing },
        audio: false,
      });
      cameraStreamRef.current = stream;
      if (cameraRef.current) {
        cameraRef.current.srcObject = stream;
        cameraRef.current.play().catch(() => {});
      }
    } catch {
      setCameraError('Acces camera refuse');
      setCameraOn(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(t => t.stop());
      cameraStreamRef.current = null;
    }
    if (cameraRef.current) cameraRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (cameraOn) startCamera(cameraFacing);
    else stopCamera();
  }, [cameraOn, cameraFacing]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => stopCamera(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // WebSocket connection
  const connectWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}/ws/mobile/${streamerId}`);
    wsRef.current = ws;

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'status') {
          const running = data.running ?? false;
          const s = data.status;
          const normalized = (!running && (s === 'error' || s === 'disconnected')) ? 'idle' : s;
          setLiveStatus(normalized);
          setLiveRunning(running);
          if (data.message) setLiveMsg(data.message);
        } else if (data.type === 'animation') {
          const animEvent = normalizeAnimEvent(data);
          const atype = animEvent.animation_type ?? 'video';
          if (atype === 'text' || atype === 'text_dynamic') {
            showTextAnimMobile(animEvent);
          } else {
            enqueueAnim(animEvent);
          }
        } else if (data.type === 'audio' && data.file) {
          const audio = new Audio(`/api/animations/${data.file}`);
          audio.volume = Number(data.volume ?? 1);
          audio.play().catch(() => {});
        } else if (data.action === 'play_animation') {
          const animEvent = normalizeAnimEvent(data);
          const atype = animEvent.animation_type ?? 'video';
          if (atype === 'text' || atype === 'text_dynamic') {
            showTextAnimMobile(animEvent);
          } else {
            enqueueAnim(animEvent);
          }
        } else if (data.action === 'play_audio' && data.file) {
          const audio = new Audio(`/api/animations/${data.file}`);
          audio.volume = Number(data.volume ?? 1);
          audio.play().catch(() => {});
        } else if (data.action === 'mobile_comments_state') {
          setMobileCommentsEnabled(!!data.enabled);
          setMobileCommentsTts(!!data.tts_enabled);
          if (data.config) setMobileCommentsCfg(data.config);
        } else if (data.action === 'mobile_comment') {
          if (data.config) setMobileCommentsCfg(data.config);
          setMobileCommentsEnabled(true);
          setMobileCommentsTts(!!data.tts_enabled);
          const item = { username: data.username, nickname: data.nickname, avatar_url: data.avatar_url, text: data.text };
          setMobileComments(prev => [...prev, item].slice(-50));
          if (data.tts_enabled && data.text) {
            const spoken = `${data.nickname || data.username} dit ${data.text}`;
            const voiceId = data.config?.mobile_comment_voice_id;
            if (voiceId) {
              ttsElevenLabs(voiceId, spoken).then(r => {
                const url = URL.createObjectURL(r.data);
                const audio = new Audio(url);
                audio.play().finally(() => setTimeout(() => URL.revokeObjectURL(url), 15000));
              }).catch(() => {
                try { if ('speechSynthesis' in window) { const u = new SpeechSynthesisUtterance(spoken); u.lang = 'fr-FR'; window.speechSynthesis.speak(u); } } catch {}
              });
            } else {
              try { if ('speechSynthesis' in window) { const u = new SpeechSynthesisUtterance(spoken); u.lang = 'fr-FR'; window.speechSynthesis.speak(u); } } catch {}
            }
          }
        } else if (data.action === 'queue_update') {
          setQData({ enabled: !!data.enabled, paused: !!data.paused, config: data.config || {}, items: data.items || [] });
          if (data.paused) setQActive(null);
          else if (data.active) setQActive(data.active);
        } else if (data.action === 'queue_show') {
          setQData(prev => prev ? { ...prev, config: data.config || prev.config } : { enabled: true, paused: false, config: data.config || {}, items: [] });
          setQActive(data.item || null); setQTick(t => t + 1);
        } else if (data.action === 'queue_hide') {
          setQActive(null);
        } else if (data.action === 'queue_test') {
          setQData({ enabled: true, paused: false, config: data.config || {}, items: data.items || [] });
          setQActive(data.item || null); setQTick(t => t + 1);
          if (qHideRef.current) clearTimeout(qHideRef.current);
          qHideRef.current = setTimeout(() => setQActive(null), data.duration || 9000);
        } else if (data.action === 'banners_state') {
          setBanners(Array.isArray(data.banners) ? data.banners : []);
        }
      } catch {}
    };

    ws.onclose = () => {
      reconnectTimer.current = setTimeout(connectWs, 3000);
    };
    ws.onerror = () => ws.close();
  }, [streamerId]);

  useEffect(() => {
    loadStatus();
    connectWs();
    return () => {
      wsRef.current?.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [streamerId, connectWs, loadStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Animation queue
  const enqueueAnim = (anim: AnimEvent) => {
    queueRef.current.push(anim);
    if (!playingRef.current) playNext();
  };

  const playNext = () => {
    if (queueRef.current.length === 0) {
      playingRef.current = false;
      setCurrentAnim(null);
      return;
    }
    playingRef.current = true;
    const anim = queueRef.current.shift()!;
    setCurrentAnim(anim);
    setLastGift(anim.gift_name ?? null);
  };

  useEffect(() => {
    if (!currentAnim) return;
    const atype = currentAnim.animation_type ?? 'video';
    if (atype === 'text' || atype === 'text_dynamic') {
      const dur = currentAnim.text_duration ?? 5000;
      showTextAnimMobile(currentAnim);
      const timer = setTimeout(() => playNext(), dur);
      return () => clearTimeout(timer);
    }
    const video = videoRef.current;
    if (!video) return;
    video.src = animationFileUrl(currentAnim.animation_file ?? currentAnim.file ?? '');
    video.load();
    video.play().catch(() => {});
  }, [currentAnim]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVideoEnd = () => playNext();

  const showTextAnimMobile = (anim: AnimEvent) => {
    const id = ++textAnimIdRef.current;
    setTextAnims(prev => [...prev, { id, anim }]);
    const dur = anim.text_duration ?? 5000;
    setTimeout(() => {
      setTextAnims(prev => prev.filter(t => t.id !== id));
    }, dur);
  };

  // Envoi du texte dynamique
  const handleSendDynamicText = async () => {
    const hasCfg = selectedDynAnim || dynamicTextConfigs.length > 0;
    if (!textInput.trim() || !hasCfg) return;
    const cfg = selectedDynAnim
      ? { pos_x: selectedDynAnim.pos_x, pos_y: selectedDynAnim.pos_y, pos_w: selectedDynAnim.pos_w, pos_h: selectedDynAnim.pos_h }
      : { pos_x: dynamicTextConfigs[0].pos_x, pos_y: dynamicTextConfigs[0].pos_y, pos_w: dynamicTextConfigs[0].pos_w, pos_h: dynamicTextConfigs[0].pos_h };
    setTextSending(true);
    try {
      // Récupérer les paramètres bg depuis la config de l'animation dynamique sélectionnée
      const bgSrc = selectedDynAnim ?? (dynamicTextConfigs.length > 0 ? dynamicTextConfigs[0] : null);
      await sendDynamicText(streamerId, {
        text: textInput.trim(),
        text_color: dynTextColor,
        text_size: dynTextSize,
        text_font: bgSrc?.text_font ?? 'sans-serif',
        text_effect: dynTextEffect,
        text_loops: bgSrc?.text_loops ?? 1,
        text_duration: dynTextDuration * 1000,
        text_bg_color: bgSrc?.text_bg_color ?? '#000000',
        text_bg_opacity: bgSrc?.text_bg_opacity ?? 0,
        text_bg_anim: bgSrc?.text_bg_anim ?? 'none',
        pos_x: cfg.pos_x,
        pos_y: cfg.pos_y,
        pos_w: cfg.pos_w,
        pos_h: cfg.pos_h,
      });
      setTextInput('');
      setShowTextPanel(false);
      setSelectedDynAnim(null);
    } catch {}
    setTextSending(false);
  };

  // Declenchement manuel d une animation depuis la table de mixage
  const handleTriggerManual = async (anim: ManualAnimation) => {
    if (anim.animation_type === 'text_dynamic') {
      setDynTextSize(anim.text_size ?? 48);
      setDynTextColor(anim.text_color ?? '#ffffff');
      setDynTextEffect(anim.text_effect ?? 'fade');
      setDynTextDuration(Math.round((anim.text_duration ?? 5000) / 1000));
      setSelectedDynAnim(anim);
      setShowTextPanel(true);
      return;
    }
    if (triggeringId) return;
    setTriggeringId(anim.id);
    setActivePadId(anim.id);
    try {
      await triggerManualAnimation(streamerId, anim.id);
    } catch {}
    setTriggeringId(null);
    setTimeout(() => setActivePadId(id => id === anim.id ? null : id), 600);
  };


  const statusColor = {
    idle: '#64748b', connecting: '#f59e0b',
    connected: '#16a34a', error: '#dc2626', disconnected: '#94a3b8',
  }[liveStatus] ?? '#64748b';

  const statusLabel = {
    idle: 'En attente', connecting: 'Connexion...',
    connected: 'Live connecte', error: 'Erreur', disconnected: 'Deconnecte',
  }[liveStatus] ?? '';

  const stageStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100vw',
    height: '100dvh',
    minHeight: '100vh',
    zIndex: 1,
    overflow: 'hidden',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000',
      height: '100dvh', minHeight: '100vh',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      userSelect: 'none',
      overflow: 'hidden',
    }}>
      {/* ══ Canvas plein écran ══ */}
      <video
        ref={cameraRef}
        muted
        playsInline
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover',
          transform: cameraFacing === 'user' ? 'scaleX(-1)' : 'none',
          display: cameraOn ? 'block' : 'none',
          zIndex: 0,
        }}
      />
      <div style={stageStyle}>
        {/* Overlay image */}
        {overlayUrl && (
          <img src={overlayUrl} alt="overlay"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', zIndex: 1, pointerEvents: 'none' }} />
        )}
        {/* Éléments statiques dessous */}
        {staticElems.filter(e => e.layer === 'below').sort((a,b) => a.sort_order - b.sort_order).map(el => (
          <img key={el.id} src={`/api/animation-files/${el.image_file}`} alt=""
            style={{ position: 'absolute', left: `${el.pos_x}%`, top: `${el.pos_y}%`, width: `${el.pos_w}%`, height: `${el.pos_h}%`, objectFit: 'contain', zIndex: 2, pointerEvents: 'none' }} />
        ))}
        {/* Animation vidéo */}
        {currentAnim && (currentAnim.animation_type ?? 'video') === 'video' && (
          <div style={{
            position: 'absolute',
            left: `${currentAnim.pos_x}%`, top: `${currentAnim.pos_y}%`,
            width: `${currentAnim.pos_w}%`, height: `${currentAnim.pos_h}%`,
            zIndex: 3,
          }}>
            <video
              ref={videoRef}
              muted={false}
              playsInline
              onEnded={handleVideoEnd}
              style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
            />
          </div>
        )}
        {/* Animations texte */}
        {textAnims.map(({ id, anim }) => {
          const effect = anim.text_effect ?? 'fade';
          const loops = anim.text_loops ?? 1;
          const loopDur = anim.text_duration ?? 5000;
          const totalDur = loopDur * loops;
          const bounce = effect === 'bounce';
          const singleDur = bounce ? Math.min(loopDur / 1000, 2) : loopDur / 1000;
          const iterCount = bounce ? Math.ceil(totalDur / (singleDur * 1000)) : loops;
          const fillMode = (effect === 'bounce' || effect.startsWith('scroll')) ? 'none' : 'forwards';
          const textAnim = `mo-${effect} ${singleDur}s ease ${iterCount} ${fillMode}`;
          const bgOpacity = (anim.text_bg_opacity ?? 0) / 100;
          const bgAnim = anim.text_bg_anim ?? 'none';
          const hasBg = bgOpacity > 0;
          const bgCss = hasBg && bgAnim !== 'none' ? `mo-bg-${bgAnim} 0.4s ease forwards` : 'none';
          const bgDelay = hasBg && bgAnim !== 'none' ? 0.4 : 0;
          let bgRgba = 'transparent';
          if (hasBg) {
            const c = anim.text_bg_color ?? '#000000';
            const r = parseInt(c.slice(1,3),16), g = parseInt(c.slice(3,5),16), b = parseInt(c.slice(5,7),16);
            bgRgba = `rgba(${r},${g},${b},${bgOpacity})`;
          }
          return (
            <div key={id} className="mo-text-anim-wrap"
              style={{ left: `${anim.pos_x}%`, top: `${anim.pos_y}%`, width: `${anim.pos_w}%`, height: `${anim.pos_h}%`, zIndex: 4 }}>
              {hasBg && <div className="mo-text-anim-bg" style={{ background: bgRgba, animation: bgCss }} />}
              <div className="mo-text-anim"
                style={{ fontSize: spx(anim.text_size ?? 48, 0.35), color: anim.text_color ?? '#ffffff', fontFamily: anim.text_font ?? 'sans-serif', animation: textAnim, animationDelay: `${bgDelay}s`, lineHeight: '1.2' }}>
                {anim.text}
              </div>
            </div>
          );
        })}
        {/* Images cadeaux permanentes */}
        {permanentGifts.map((g, i) => (
          <img
            key={i}
            src={`/api/gift-image?url=${encodeURIComponent(g.gift_image_url)}`}
            alt={g.gift_name ?? 'cadeau'}
            style={{
              position: 'absolute',
              left: `${g.gift_img_x}%`, top: `${g.gift_img_y}%`,
              width: `${g.gift_img_w}%`, height: `${g.gift_img_h}%`,
              objectFit: 'contain', zIndex: 5, pointerEvents: 'none',
              borderRadius: 8, filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.6))',
            }}
          />
        ))}
        {/* Éléments statiques dessus */}
        {staticElems.filter(e => e.layer === 'above').sort((a,b) => a.sort_order - b.sort_order).map(el => (
          <img key={el.id} src={`/api/animation-files/${el.image_file}`} alt=""
            style={{ position: 'absolute', left: `${el.pos_x}%`, top: `${el.pos_y}%`, width: `${el.pos_w}%`, height: `${el.pos_h}%`, objectFit: 'contain', zIndex: 6, pointerEvents: 'none' }} />
        ))}

        {banners
          .slice()
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .map(b => <MobileBanner key={b.theme_animation_id} banner={b} spx={spx} />)}

        {mobileCommentsEnabled && mobileCommentsCfg && mobileComments.length > 0 && (
          <div style={{
            position: 'absolute',
            left: `${mobileCommentsCfg.pos_x ?? 3}%`,
            top: `${mobileCommentsCfg.pos_y ?? 68}%`,
            width: `${mobileCommentsCfg.pos_w ?? 58}%`,
            height: `${mobileCommentsCfg.pos_h ?? 23}%`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            gap: 6,
            overflow: 'hidden',
            padding: (mobileCommentsCfg.text_bg_opacity ?? 35) > 0 ? '8px 10px' : 0,
            borderRadius: (mobileCommentsCfg.text_bg_opacity ?? 35) > 0 ? 14 : 0,
            background: (mobileCommentsCfg.text_bg_opacity ?? 35) > 0 ? `${mobileCommentsCfg.text_bg_color ?? '#000000'}${Math.round(((mobileCommentsCfg.text_bg_opacity ?? 35) / 100) * 255).toString(16).padStart(2, '0')}` : 'transparent',
            fontFamily: mobileCommentsCfg.text_font ?? 'sans-serif',
            fontSize: spx(mobileCommentsCfg.text_size ?? 28, 0.45, 11),
            color: mobileCommentsCfg.text_color ?? '#fbbf24',
            zIndex: 9,
            pointerEvents: 'none',
          }}>
            {mobileComments.slice(-Math.max(1, Number(mobileCommentsCfg.queue_max_visible ?? 5))).map((c, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, lineHeight: 1.25, textShadow: '0 1px 3px rgba(0,0,0,.85)' }}>
                {(mobileCommentsCfg.queue_show_avatar ?? 1) !== 0 && c.avatar_url && <img src={`/api/gift-image?url=${encodeURIComponent(c.avatar_url)}`} alt="" style={{ width: '1.45em', height: '1.45em', borderRadius: '50%', objectFit: 'cover', flex: 'none' }} />}
                <div><span style={{ fontWeight: 900, marginRight: '.25em' }}>{c.nickname || c.username}</span><span style={{ fontWeight: 650 }}>{c.text}</span></div>
              </div>
            ))}
          </div>
        )}

        {/* File d'attente des questions */}
        {qData && qData.enabled && (() => {
          const c = qData.config || {};
          const nv = (v: any, d: number) => (v === undefined || v === null || v === '' ? d : Number(v));
          const tr = (v: any) => v !== 0 && v !== false && v !== undefined && v !== null;
          const qf = (n?: string) => (n ? `/api/animation-file/${n}` : '');
          const tk = (u?: string) => (u && /tiktokcdn|ibyteimg|muscdn|bytecdn|tiktok\.com/i.test(u) ? '/api/gift-image?url=' + encodeURIComponent(u) : (u || ''));
          const lx = nv(c.pos_x, 2), ly = nv(c.pos_y, 55), lw = nv(c.pos_w, 34), lh = nv(c.pos_h, 42);
          const qx = nv(c.queue_q_pos_x, 15), qy = nv(c.queue_q_pos_y, 38), qw = nv(c.queue_q_pos_w, 70), qh = nv(c.queue_q_pos_h, 24);
          const cx = nv(c.queue_ctrl_pos_x, 2), cy = nv(c.queue_ctrl_pos_y, 89), cw = nv(c.queue_ctrl_pos_w, 34), chh = nv(c.queue_ctrl_pos_h, 8);
          const color = c.text_color || '#fff', font = c.text_font || 'sans-serif';
          const tsize = nv(c.text_size, 40);
          const listFs = spx(tsize, 0.34, 12);
          const fitQuestionFs = () => {
            const text = `${qActive?.nickname || qActive?.username || ''} ${qActive?.question || ''}`.trim();
            const chars = Math.max(1, text.length);
            const maxLines = qh < 13 ? 1 : qh < 21 ? 2 : 3;
            const base = Math.min(tsize * 0.5, qh * 2.1);
            const capacity = Math.max(8, qw * 1.18 * maxLines);
            const fs = chars > capacity ? base * (capacity / chars) : base;
            return spx(Math.max(7, Math.min(tsize * 0.5, fs)), 1, 11);
          };
          const qFs = fitQuestionFs();
          const showAvatar = tr(c.queue_show_avatar), showCounter = tr(c.queue_show_counter);
          const bgImg = c.queue_bg_image ? qf(c.queue_bg_image) : '';
          const qBg = c.queue_q_bg_image ? qf(c.queue_q_bg_image) : '';
          const qBgColor = `${c.queue_q_bg_color || c.text_bg_color || '#111111'}${Math.round((Math.max(0, Math.min(100, nv(c.text_bg_opacity, 75))) / 100) * 255).toString(16).padStart(2, '0')}`;
          const appear = c.queue_appear_anim || 'fade';
          const max = nv(c.queue_max_visible, 5);
          const waiting = (qData.items || []).filter((i: any) => i.status === 'waiting');
          const paused = qData.paused;
          const showGift = tr(c.queue_show_gift) && c.gift_image_url;
          const showPGift = tr(c.queue_show_pgift) && c.priority_gift_image_url;
          const gpx = nv(c.queue_gift_pos_x, 70), gpy = nv(c.queue_gift_pos_y, 5), gpw = nv(c.queue_gift_pos_w, 18), gph = nv(c.queue_gift_pos_h, 10);
          const ppx = nv(c.queue_pgift_pos_x, 70), ppy = nv(c.queue_pgift_pos_y, 18), ppw = nv(c.queue_pgift_pos_w, 18), pph = nv(c.queue_pgift_pos_h, 10);
          const post = (p: string) => { try { fetch(`/nsstream/${streamerId}/queue/${p}`, { method: 'POST' }).catch(() => {}); } catch { /* noop */ } };
          return (
            <>
              {!paused && (
                <div style={{ position: 'absolute', left: `${lx}%`, top: `${ly}%`, width: `${lw}%`, maxHeight: `${lh}%`, zIndex: 7, display: 'flex', flexDirection: 'column', gap: 5, boxSizing: 'border-box', fontFamily: font, color, fontSize: listFs, padding: bgImg ? '6% 8%' : 0, background: bgImg ? `url(${bgImg}) center/100% 100% no-repeat` : 'transparent', overflow: 'hidden', pointerEvents: 'none' }}>
                  {showCounter && <div style={{ fontWeight: 800, textShadow: '0 1px 3px rgba(0,0,0,.7)' }}>💬 File · {waiting.length}</div>}
                  {waiting.slice(0, max).map((it: any, i: number) => (
                    <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,.32)', borderRadius: 99, padding: '3px 8px' }}>
                      <span style={{ fontWeight: 800, opacity: .7 }}>{i + 1}</span>
                      {showAvatar && it.avatar_url && <img src={tk(it.avatar_url)} alt="" style={{ width: '1.5em', height: '1.5em', borderRadius: '50%', objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                      <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textShadow: '0 1px 2px rgba(0,0,0,.6)' }}>{it.nickname || it.username}</span>
                      {it.priority ? <span>⭐</span> : null}
                    </div>
                  ))}
                </div>
              )}
              {!paused && qActive && (
                <div key={qTick} style={{ position: 'absolute', left: `${qx}%`, top: `${qy}%`, width: `${qw}%`, height: `${qh}%`, zIndex: 8, boxSizing: 'border-box', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '5%', textAlign: 'center', fontFamily: font, color, background: qBg ? `url(${qBg}) center/100% 100% no-repeat` : qBgColor, animation: `qmo-${appear} .5s ease both`, pointerEvents: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {showAvatar && qActive.avatar_url && <img src={tk(qActive.avatar_url)} alt="" style={{ width: '1.8em', height: '1.8em', borderRadius: '50%', objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                    <span style={{ fontWeight: 800, fontSize: Math.round(qFs * 0.6), textShadow: '0 1px 3px rgba(0,0,0,.75)' }}>{qActive.nickname || qActive.username}</span>
                  </div>
                  <span style={{ fontWeight: 800, fontSize: qFs, lineHeight: 1.25, textShadow: '0 1px 3px rgba(0,0,0,.75)' }}>{qActive.question}</span>
                </div>
              )}
              {!paused && showGift && (
                <div style={{ position: 'absolute', left: `${gpx}%`, top: `${gpy}%`, width: `${gpw}%`, height: `${gph}%`, zIndex: 7, pointerEvents: 'none', overflow: 'visible' }}>
                  <img src={tk(c.gift_image_url)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,.55))' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  {c.queue_gift_label && (c.queue_gift_label_pos || 'none') !== 'none' && (
                    <span style={{ position: 'absolute', whiteSpace: 'nowrap', fontWeight: 800, color: c.queue_gift_label_color || '#fff', fontSize: spx(nv(c.queue_gift_label_size, 14)), fontFamily: font, textShadow: '0 1px 3px rgba(0,0,0,.7)', ...gLabelStyle(c.queue_gift_label_pos) }}>{c.queue_gift_label}</span>
                  )}
                </div>
              )}
              {!paused && showPGift && (
                <div style={{ position: 'absolute', left: `${ppx}%`, top: `${ppy}%`, width: `${ppw}%`, height: `${pph}%`, zIndex: 7, pointerEvents: 'none', overflow: 'visible' }}>
                  <img src={tk(c.priority_gift_image_url)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,.55))' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  {c.queue_pgift_label && (c.queue_pgift_label_pos || 'none') !== 'none' && (
                    <span style={{ position: 'absolute', whiteSpace: 'nowrap', fontWeight: 800, color: c.queue_pgift_label_color || '#fff', fontSize: spx(nv(c.queue_pgift_label_size, 14)), fontFamily: font, textShadow: '0 1px 3px rgba(0,0,0,.7)', ...gLabelStyle(c.queue_pgift_label_pos) }}>{c.queue_pgift_label}</span>
                  )}
                </div>
              )}
              <div style={{ position: 'absolute', left: `${cx}%`, top: `${cy}%`, width: `${cw}%`, height: `${chh}%`, zIndex: 9, display: 'flex', gap: 6 }}>
                <button onClick={() => post('toggle')} style={{ flex: 1, border: '1px solid rgba(255,255,255,.18)', borderRadius: 12, background: 'rgba(70,70,78,.5)', color: 'rgba(255,255,255,.85)', fontWeight: 700, fontSize: '4.5vw', cursor: 'pointer' }}>{paused ? '▶' : '⏹'}</button>
                <button onClick={() => post('next')} style={{ flex: 1, border: '1px solid rgba(255,255,255,.18)', borderRadius: 12, background: 'rgba(70,70,78,.5)', color: 'rgba(255,255,255,.85)', fontWeight: 700, fontSize: '4.5vw', cursor: 'pointer' }}>⏭</button>
              </div>
            </>
          );
        })()}
      </div>

      {/* ══ Status bar ══ */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 30,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'env(safe-area-inset-top, 12px) 14px 10px',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, boxShadow: `0 0 6px ${statusColor}` }} />
          <span style={{ color: '#fff', fontSize: 11, fontWeight: 600, opacity: 0.9 }}>{statusLabel}</span>
          {liveMsg ? <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10 }}>— {liveMsg}</span> : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {cameraOn && (
            <button onClick={() => setCameraFacing(f => f === 'user' ? 'environment' : 'user')}
              style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backdropFilter: 'blur(4px)' }}>
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
          <button onClick={() => setCameraOn(v => !v)}
            style={{ background: cameraOn ? 'rgba(37,99,235,0.7)' : 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backdropFilter: 'blur(4px)' }}>
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
            </svg>
          </button>
          {displayName && (
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 600 }}>@{displayName}</span>
          )}
        </div>
      </div>
      {cameraError && (
        <div style={{ position: 'absolute', top: 60, left: '50%', transform: 'translateX(-50%)', background: 'rgba(220,38,38,0.85)', borderRadius: 8, padding: '6px 14px', zIndex: 31 }}>
          <span style={{ color: '#fff', fontSize: 11 }}>{cameraError}</span>
        </div>
      )}


      {/* ══ Tiroir Table de mixage (slide-up) ══ */}
      {showMixer && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 35,
          background: 'rgba(10,10,20,0.97)', backdropFilter: 'blur(16px)',
          borderTopLeftRadius: 22, borderTopRightRadius: 22,
          padding: '10px 12px env(safe-area-inset-bottom, 80px)',
          maxHeight: '70vh', overflowY: 'auto',
          boxShadow: '0 -4px 40px rgba(0,0,0,0.6)',
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          {/* Poignée + titre */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>🎛️ Table de mixage</div>
            <button onClick={() => setShowMixer(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 30, height: 30, color: '#fff', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          </div>
          {manualAnims.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: 12, paddingTop: 20, lineHeight: 2 }}>
              Aucune animation manuelle<br />
              <span style={{ fontSize: 10 }}>Configurez le theme actif</span>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {manualAnims.map(anim => {
                const isActive = activePadId === anim.id;
                const isTrig = triggeringId === anim.id;
                const colors: Record<string, { bg: string; border: string; active: string; icon: string }> = {
                  video: { bg: 'rgba(37,99,235,0.18)', border: 'rgba(37,99,235,0.5)', active: 'rgba(37,99,235,0.7)', icon: '🎬' },
                  audio: { bg: 'rgba(22,163,74,0.18)', border: 'rgba(22,163,74,0.5)', active: 'rgba(22,163,74,0.7)', icon: '🔊' },
                  text: { bg: 'rgba(234,88,12,0.18)', border: 'rgba(234,88,12,0.5)', active: 'rgba(234,88,12,0.7)', icon: '📝' },
                  text_dynamic: { bg: 'rgba(124,58,237,0.18)', border: 'rgba(124,58,237,0.5)', active: 'rgba(124,58,237,0.7)', icon: '⌨️' },
                };
                const c = colors[anim.animation_type] ?? { bg: 'rgba(100,116,139,0.2)', border: 'rgba(100,116,139,0.5)', active: 'rgba(100,116,139,0.7)', icon: '▶' };
                return (
                  <button key={anim.id} onClick={() => handleTriggerManual(anim)} disabled={isTrig}
                    style={{ background: isActive ? c.active : c.bg, border: `1px solid ${isActive ? 'rgba(255,255,255,0.4)' : c.border}`, borderRadius: 14, padding: '16px 12px', cursor: isTrig ? 'wait' : 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, transition: 'all 0.15s', transform: isActive ? 'scale(0.96)' : 'scale(1)', minHeight: 90 }}>
                    <span style={{ fontSize: 28 }}>{c.icon}</span>
                    <span style={{ color: '#fff', fontSize: 11, fontWeight: 600, textAlign: 'center', lineHeight: 1.3, wordBreak: 'break-word' }}>{anim.name}</span>
                    {anim.animation_type === 'text_dynamic' && <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 9 }}>Appui = saisir texte</span>}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <QuestionQueuePanel streamerId={streamerId} isMobile />
          </div>
        </div>
      )}

      {/* ══ Panneau texte dynamique ══ */}
      {showTextPanel && (
        <div style={{
          position: 'absolute', bottom: 80, left: 12, right: 12, zIndex: 40,
          background: 'rgba(15,23,42,0.95)', backdropFilter: 'blur(12px)',
          borderRadius: 16, padding: '14px 14px 10px', border: '1px solid rgba(255,255,255,0.12)',
        }}>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.6 }}>Texte a afficher</div>
          <textarea
            value={textInput}
            onChange={e => setTextInput(e.target.value)}
            placeholder="Saisir votre texte..."
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, color: '#fff',
              fontSize: 15, padding: '10px 12px', resize: 'none', minHeight: 60,
              fontFamily: 'system-ui, sans-serif', outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
            <input type="color" value={dynTextColor} onChange={e => setDynTextColor(e.target.value)}
              style={{ width: 36, height: 34, padding: 2, border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />
            <input type="number" value={dynTextSize} min={16} max={200} onChange={e => setDynTextSize(Number(e.target.value))}
              style={{ width: 56, padding: '7px 6px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 13, textAlign: 'center', outline: 'none' }} />
            <input type="number" value={dynTextDuration} min={1} max={60} step={1} onChange={e => setDynTextDuration(Number(e.target.value))}
              style={{ width: 48, padding: '7px 6px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 13, textAlign: 'center', outline: 'none' }}
              title="Duree (s)" />
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>s</span>
            <select value={dynTextEffect} onChange={e => setDynTextEffect(e.target.value)}
              style={{ flex: 1, padding: '7px 6px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(30,41,59,0.9)', color: '#fff', fontSize: 12, outline: 'none' }}>
              <option value="fade">Fondu</option>
              <option value="scroll-left">Gauche</option>
              <option value="scroll-right">Droite</option>
              <option value="scroll-up">Haut</option>
              <option value="scroll-down">Bas</option>
              <option value="typewriter">Clavier</option>
              <option value="bounce">Rebond</option>
              <option value="zoom">Zoom</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={() => { setShowTextPanel(false); setSelectedDynAnim(null); }} style={{
              flex: 1, padding: '10px', borderRadius: 10, border: 'none',
              background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, cursor: 'pointer',
            }}>Annuler</button>
            <button onClick={handleSendDynamicText} disabled={textSending || !textInput.trim()} style={{
              flex: 2, padding: '10px', borderRadius: 10, border: 'none',
              background: textSending || !textInput.trim() ? 'rgba(124,58,237,0.4)' : 'rgba(124,58,237,0.9)',
              color: '#fff', fontSize: 13, fontWeight: 700, cursor: textSending || !textInput.trim() ? 'not-allowed' : 'pointer',
            }}>{textSending ? 'Envoi...' : 'Envoyer'}</button>
          </div>
        </div>
      )}

      {/* Last gift toast */}
      {lastGift && liveStatus === 'connected' && (
        <div style={{
          position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)',
          borderRadius: 20, padding: '5px 14px', zIndex: 20,
          color: '#fff', fontSize: 11, whiteSpace: 'nowrap',
        }}>
          🎁 {lastGift}
        </div>
      )}

      {/* ══ Contrôles du bas ══ */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 30,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '14px 20px env(safe-area-inset-bottom, 16px)',
        background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent)',
        gap: 12,
      }}>
        {/* Bouton mixer */}
        {manualAnims.length > 0 && (
          <button onClick={() => setShowMixer(v => !v)} style={{
            width: 46, height: 46, borderRadius: '50%', border: 'none',
            background: showMixer ? 'rgba(124,58,237,0.9)' : 'rgba(255,255,255,0.15)',
            color: '#fff', fontSize: 20, cursor: 'pointer',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>🎛️</button>
        )}

      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        ${TEXT_ANIMATION_CSS}
      `}</style>
    </div>
  );
}
